/**
 * PRP-INTAKE-2 web client: the project Intake page's reads and answers.
 *
 * Its own module because `mcpApi.ts` sits at the god-file cap. Same shared `api` instance:
 * Firebase token attached, `response.data` unwrapped, failures arrive as `ApiError`
 * (`status` = HTTP status).
 */
import { api } from './api';

/** PRP-INTAKE-2: where a requirement's value came from; null while it is open. */
export type IntakeSource =
  | 'document'
  | 'photo'
  | 'floor_plan'
  | 'notes'
  | 'imported_estimate'
  | 'inference'
  | 'answered'
  | 'default';
/** PRP-INTAKE-2: one requirement on the Intake page, filled or not. */
export interface IntakeRequirement {
  id: string;
  question: string;
  input: {
    type: 'choice' | 'multi' | 'text' | 'number' | 'bool';
    choices?: { choice: number; label: string }[];
    unit?: string;
  };
  value?: unknown;
  value_label?: string | null;
  source?: IntakeSource | null;
  status: 'filled' | 'open' | 'assumed';
  recommended?: number | null;
  reason?: string | null;
}
/** PRP-INTAKE-2: a room, an elevation, or a house-wide group within a stage. */
export interface IntakeGroup {
  group: string;
  kind: 'claim' | 'structure' | 'exterior' | 'elevation' | 'interior' | 'room' | 'area';
  requirements: IntakeRequirement[];
}
/** PRP-INTAKE-2: GET /mcp/projects/{id}/intake, every requirement that applies, in stage order. */
export interface ProjectIntake {
  project_id: string;
  loss_type?: string | null;
  stage_order?: 'top_down' | 'bottom_up' | 'from_source' | null;
  jurisdiction?: { state?: string | null; matching_status?: string | null; note?: string | null } | null;
  /** The project's name (absent on older servers). */
  project_name?: string | null;
  open_count: number;
  stages: { stage: number; title: string; groups: IntakeGroup[] }[];
}
/** PRP-INTAKE-2: one answer for POST /mcp/projects/{id}/interview/answer. */
export interface IntakeAnswer {
  id: string;
  choice?: number;
  choices?: number[];
  value?: string | number;
}
/** One answer's outcome; `rejected` carries the reason. Other statuses mean it was taken. */
export interface IntakeAnswerResult {
  id: string;
  status: string;
  reason?: string | null;
  next?: string | null;
}
/** POST /mcp/projects/{id}/interview/answer. */
export interface IntakeAnswerResponse {
  results?: IntakeAnswerResult[];
  open_count?: number;
}

export const IntakeAPI = {
  /** Every requirement that applies to the project, filled or open, by stage. */
  getIntake: (projectId: string): Promise<ProjectIntake> =>
    api.get(`/mcp/projects/${encodeURIComponent(projectId)}/intake`),

  /** Answer a batch of intake requirements (the interview answer route, as the chat does). */
  answerIntake: (projectId: string, answers: IntakeAnswer[]): Promise<IntakeAnswerResponse> =>
    api.post(`/mcp/projects/${encodeURIComponent(projectId)}/interview/answer`, { answers }),
};
